import { downloadLatestInvoiceReceipt } from '../services/invoiceReceiptApi';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Link } from 'react-router-dom';
import DeliveryPanel from './DeliveryPanel';
import InvoicePreview from './InvoicePreview';
import ProductMappingPanel from './ProductMappingPanel';
import { mappingStatus, matchesMapping, productIndex, referenceId } from '../utils/productMapping';
import { deliveryStatus, displayDate, localToday, mergeDelivery, isDeliveryReviewed } from '../utils/delivery';
import RunningSaleItems from './RunningSaleItems';
import './RunningSaleReport.css';
import { sumRupees } from '../utils/inr';
import { expandInvoiceItems, filterItemRows, sortItemRows, quantityTotals, summarizeProducts, itemExportTable, downloadCsv } from '../utils/runningSalesReport';

const inr = (n) => (Number(n || 0)).toLocaleString('en-IN');
const ymd = (value) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

function startOfYearYMD() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

function parseDateOnly(s) {
  try {
    if (!s) return null;
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    if (!Number.isFinite(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  } catch {
    return null;
  }
}

export default function RunningSaleReport() {
  const today = localToday();
  useEffect(() => {
    if (window.location.hash === '#delivery-management') document.getElementById('delivery-management')?.scrollIntoView?.({ block: 'start' });
  }, []);

  const [dateFrom, setDateFrom] = useState(startOfYearYMD());
  const [dateTo, setDateTo] = useState(today);
  const [org, setOrg] = useState('All');
  const [rows, setRows] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [due, setDue] = useState('All');
  const [q, setQ] = useState('');
  const [deliveryId, setDeliveryId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const deepLinkOpened = useRef(false);
  useEffect(() => {
    if (deepLinkOpened.current || !hasLoaded) return;
    const query = new URLSearchParams(window.location.search);
    const id = query.get('invoiceId');
    if (!id) return;
    deepLinkOpened.current = true;
    const invoice = rows.find(row => String(row._id) === id);
    if (!invoice) { setLoadError('Requested invoice was not found. Refresh the report before retrying.'); return; }
    if (query.get('panel') === 'delivery') setDeliveryId(invoice._id);
    else setViewing(invoice);
  }, [rows, hasLoaded]);

  const [mappingId, setMappingId] = useState(null);
  const [mappingFilter, setMappingFilter] = useState('All');
  const [catalogReady, setCatalogReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const updateDelivery = useCallback(update => {
    setRows(previous => previous.map(invoice => invoice._id === update._id ? mergeDelivery(invoice, update) : invoice));
  }, []);

  const updateMappings = useCallback(snapshot => {
    setRows(previous => previous.map(invoice => invoice._id === snapshot._id ? snapshot : invoice));
    setProducts(previous => {
      const indexed = productIndex(previous);
      for (const item of snapshot.items) {
        const id = referenceId(item.productId);
        if (!id) continue;
        if (item.currentProduct) indexed.set(id, { ...indexed.get(id), ...item.currentProduct });
        else indexed.delete(id);
      }
      return [...indexed.values()];
    });
  }, []);
  const updateMappingCatalog = useCallback((organization, list) => {
    setProducts(previous => [...previous.filter(product => product.organization !== organization), ...list]);
  }, []);

  // REPORT MODE
  const [reportLevel, setReportLevel] = useState('invoice');

  // ITEM REPORT SORT
  const [itemSort, setItemSort] = useState('item');
  const [itemView, setItemView] = useState('rows');
  const [itemQuery, setItemQuery] = useState('');
  const [itemStatus, setItemStatus] = useState('All');
  const [products, setProducts] = useState([]);
  const [productError, setProductError] = useState('');

  async function load() {
    setLoading(true);
    setLoadError('');
    setCatalogReady(false);

    const [invoices, master] = await Promise.allSettled([
      axios.get('/api/running'), axios.get('/api/products', { params: { active: 'all' } })
    ]);
    if (invoices.status === 'fulfilled') setRows(Array.isArray(invoices.value.data) ? invoices.value.data : []);
    else setLoadError(invoices.reason.response?.data?.error || 'Unable to load invoices. Please refresh.');
    if (master.status === 'fulfilled' && Array.isArray(master.value.data)) {
      setProducts(Array.isArray(master.value.data) ? master.value.data : []); setProductError(''); setCatalogReady(true);
    } else { setProducts([]); setCatalogReady(false); setProductError('Product Master lookup failed. Invoice items remain visible; mapping completion and some SKUs cannot be verified.'); }
    setLoading(false);
    setHasLoaded(true);
  }

  useEffect(() => {
    load();
  }, []);

  // --------------------------------------------------
  // INVOICE FILTERING
  // --------------------------------------------------

  const baseFiltered = useMemo(() => {
    const f = parseDateOnly(dateFrom);
    const t = parseDateOnly(dateTo);
    const term = q.trim().toLowerCase();

    return rows.filter((r) => {
      const d = parseDateOnly(r.invoiceDate || r.date);

      if (f && d && d < f) return false;
      if (t && d && d > t) return false;

      if (
        org !== 'All' &&
        (r.organization || '-') !== org
      ) {
        return false;
      }

      // Due is intentionally calculated at INVOICE LEVEL
      const inv = Number(r.totalAmount || 0);
      const rec = Number(r.amountReceived || 0);

      const dueAmt =
        r.balanceDue != null
          ? Number(r.balanceDue)
          : Math.max(inv - rec, 0);

      if (due === 'Due' && !(dueAmt > 0)) return false;
      if (due === 'No Due' && !(dueAmt <= 0)) return false;

      if (term) {
        const hay = `
          ${r.customerName || ''}
          ${r.invoiceNumber || ''}
          ${r.whatsapp || ''}
        `.toLowerCase();

        if (!hay.includes(term)) return false;
      }

      return true;
    });
  }, [rows, dateFrom, dateTo, org, due, q]);

  const operationalInvoices = baseFiltered;
  const mappingProducts = useMemo(() => productIndex(catalogReady ? products : []), [products, catalogReady]);
  const candidateItems = useMemo(() => sortItemRows(filterItemRows(expandInvoiceItems(operationalInvoices, catalogReady ? products : []), itemQuery, itemStatus), itemSort),
    [operationalInvoices, products, catalogReady, itemQuery, itemStatus, itemSort]);
  const mappingCandidates = useMemo(() => {
    if (!itemQuery.trim() && itemStatus === 'All') return operationalInvoices;
    const ids = new Set(candidateItems.map(row => row.invoiceId));
    return operationalInvoices.filter(invoice => ids.has(invoice._id));
  }, [operationalInvoices, candidateItems, itemQuery, itemStatus]);
  const filtered = useMemo(() => mappingCandidates.filter(invoice => matchesMapping(mappingStatus(invoice, mappingProducts, catalogReady), mappingFilter)),
    [mappingCandidates, mappingProducts, catalogReady, mappingFilter]);
  const itemizedRows = useMemo(() => {
    const ids = new Set(filtered.map(invoice => invoice._id));
    return candidateItems.filter(row => ids.has(row.invoiceId));
  }, [filtered, candidateItems]);
  const fullyMapped = mappingCandidates.filter(invoice => mappingStatus(invoice, mappingProducts, catalogReady) === 'Mapped').length;
  const nextMapping = (() => {
    const current = mappingCandidates.findIndex(invoice => invoice._id === mappingId);
    return [...mappingCandidates.slice(current + 1), ...mappingCandidates.slice(0, current + 1)]
      .find(invoice => invoice._id !== mappingId && mappingStatus(invoice, mappingProducts, catalogReady) !== 'Mapped');
  })();
  const itemTotals = useMemo(() => quantityTotals(itemizedRows), [itemizedRows]);
  const productSummary = useMemo(() => summarizeProducts(itemizedRows), [itemizedRows]);
  const exportTable = () => itemExportTable(itemView === 'summary' ? productSummary : itemizedRows, itemView === 'summary');
  const nextPending = (() => {
    const current = baseFiltered.findIndex(invoice => invoice._id === deliveryId);
    const ordered = [...baseFiltered.slice(current + 1), ...baseFiltered.slice(0, current + 1)];
    return ordered.find(invoice => invoice._id !== deliveryId && deliveryStatus(invoice) !== 'Fully Delivered');
  })();


  // --------------------------------------------------
  // INVOICE TOTALS
  // --------------------------------------------------

  const totals = useMemo(() => {
    const sum = key => sumRupees(filtered.map(row => Number(row[key] || 0)), true);

    const qty = filtered.reduce(
      (s, r) =>
        s +
        (Array.isArray(r.items)
          ? r.items.reduce(
              (q, it) =>
                q + (Number(it?.qty) || 1),
              0
            )
          : 0),
      0
    );

    return {
      qty,

      invoice: sum('totalAmount'),

      received: sum('amountReceived'),

      due: filtered.reduce(
        (s, r) =>
          s +
          Number(
            r.balanceDue != null
              ? r.balanceDue
              : Math.max(
                  Number(r.totalAmount || 0) -
                    Number(r.amountReceived || 0),
                  0
                )
          ),
        0
      ),
    };
  }, [filtered]);

  // --------------------------------------------------
  // RECEIPT
  // --------------------------------------------------

  async function downloadReceipt(doc) {
    try { await downloadLatestInvoiceReceipt(doc); }
    catch (error) { alert(error.response?.data?.error || error.message || 'Unable to generate receipt PDF.'); }
  }

  // --------------------------------------------------
  // INVOICE REPORT PDF
  // --------------------------------------------------

  function exportInvoicePDF() {
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4',
    });

    const padX = 48;

    const pageW =
      pdf.internal.pageSize.getWidth();

    pdf.setFont(
      'helvetica',
      'bold'
    );

    pdf.setFontSize(16);

    pdf.text(
      'Running Sales Report',
      padX,
      48
    );

    pdf.setFont(
      'helvetica',
      'normal'
    );

    pdf.setFontSize(10);

    pdf.text(
      `Date Range: ${dateFrom} to ${dateTo}  |  Organization: ${org}  |  Due: ${due}`,
      padX,
      66
    );

    const head = [
      [
        'Date',
        'Invoice',
        'Customer',
        'WhatsApp',
        'Org',
        'Mode',
        'Qty',
        'Invoice (INR)',
        'Received (INR)',
        'Due (INR)',
      ],
    ];

    const body = filtered.map(
      (r) => [
        new Date(
          r.invoiceDate ||
            r.date
        ).toLocaleDateString(
          'en-IN'
        ),

        r.invoiceNumber ||
          r._id,

        r.customerName ||
          '-',

        r.whatsapp || '-',

        r.organization ||
          '-',

        r.paymentMode ||
          '-',

        Array.isArray(r.items)
          ? r.items.reduce(
              (q, it) =>
                q +
                (Number(
                  it?.qty
                ) || 1),
              0
            )
          : 0,

        inr(r.totalAmount),

        inr(
          r.amountReceived
        ),

        inr(
          r.balanceDue != null
            ? r.balanceDue
            : Math.max(
                Number(
                  r.totalAmount ||
                    0
                ) -
                  Number(
                    r.amountReceived ||
                      0
                  ),
                0
              )
        ),
      ]
    );

    autoTable(pdf, {
      head,
      body,
      startY: 84,

      margin: {
        left: padX,
        right: padX,
      },

      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 6,
        halign: 'left',
        valign: 'middle',
        textColor: 0,
        lineColor: 0,
      },

      headStyles: {
        fillColor: [
          255,
          255,
          255,
        ],
        textColor: 0,
        lineWidth: 0.5,
        lineColor: 0,
      },

      columnStyles: {
        6: {
          halign: 'right',
        },
        7: {
          halign: 'right',
        },
        8: {
          halign: 'right',
        },
        9: {
          halign: 'right',
        },
      },

      theme: 'grid',

      didDrawPage() {
        pdf.setFontSize(9);

        pdf.text(
          'Generated by COG India Art Foundation - Running Sales',
          padX,
          pdf.internal.pageSize.getHeight() -
            28
        );
      },
    });

    const finalY =
      pdf.lastAutoTable
        ?.finalY || 84;

    const y = finalY + 16;

    const line = (
      label,
      value,
      yy
    ) =>
      pdf.text(
        `${label}: ${inr(
          value
        )}`,
        pageW - padX,
        yy,
        {
          align: 'right',
        }
      );

    pdf.setFont(
      'helvetica',
      'bold'
    );

    pdf.text(
      `Total Qty: ${totals.qty}`,
      padX,
      y
    );

    line(
      'Total Invoice (INR)',
      totals.invoice,
      y
    );

    line(
      'Total Received (INR)',
      totals.received,
      y + 16
    );

    line(
      'Total Due (INR)',
      totals.due,
      y + 32
    );

    pdf.save(
      `RunningSales_${dateFrom}_${dateTo}${
        org === 'All'
          ? ''
          : '_' + org
      }.pdf`
    );
  }

  // --------------------------------------------------
  // ITEM REPORT PDF
  // --------------------------------------------------

  function exportItemPDF() {
    const summary = itemView === 'summary';
    const pdf = new jsPDF({ unit: 'pt', format: summary ? 'a4' : 'a3', orientation: 'landscape' });
    const table = exportTable();
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
    pdf.text(summary ? 'Product Summary' : 'Itemized Sales Report', 30, 36);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    const filterText = `Date: ${dateFrom} to ${dateTo} | Organization: ${org} | Due: ${due} | Delivery: ${itemStatus} | Product: ${itemQuery || 'All'} | Sort: ${summary ? 'Product' : itemSort}`;
    const lines = pdf.splitTextToSize(filterText, pdf.internal.pageSize.getWidth() - 60);
    pdf.text(lines, 30, 54);
    const foot = summary
      ? ['Totals', '', '', '', itemTotals.qty, itemTotals.deliveredQty, itemTotals.pendingQty]
      : ['Totals', '', '', '', '', '', itemTotals.qty, itemTotals.deliveredQty, itemTotals.pendingQty, '', '', itemTotals.value];
    autoTable(pdf, { head: [table.headers], body: table.body, foot: [foot], showFoot: 'lastPage', startY: 64 + lines.length * 11,
      margin: { left: 30, right: 30, bottom: 30 }, theme: 'grid',
      styles: { font: 'helvetica', fontSize: summary ? 8 : 7, cellPadding: 4, valign: 'middle', textColor: 0 },
      headStyles: { fillColor: [245, 245, 245], textColor: 0 },
      footStyles: { fillColor: [245, 245, 245], textColor: 0 },
      didDrawPage() { pdf.setFontSize(8); pdf.text('Unverified remainders are not confirmed production demand.', 30, pdf.internal.pageSize.getHeight() - 15); }
    });
    pdf.save(`${summary ? 'ProductSummary' : 'ItemizedSales'}_${dateFrom}_${dateTo}.pdf`);
  }

  function exportPDF() {
    if (
      reportLevel === 'item'
    ) {
      exportItemPDF();
    } else {
      exportInvoicePDF();
    }
  }

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div className="max-w-screen-2xl mx-auto p-4 md:p-6">

      {/* HEADER */}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">

        <h1 className="text-2xl font-semibold">
          Running Sales Report
        </h1>

        <div className="flex flex-wrap items-center gap-2">

          {/* REPORT LEVEL */}

          <button
            aria-pressed={reportLevel === 'invoice'}
            className={`px-3 py-2 rounded ${
              reportLevel ===
              'invoice'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100'
            }`}
            onClick={() =>
              setReportLevel(
                'invoice'
              )
            }
          >
            Invoice Level
          </button>

          <button
            aria-pressed={reportLevel === 'item'}
            className={`px-3 py-2 rounded ${
              reportLevel ===
              'item'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100'
            }`}
            onClick={() =>
              setReportLevel(
                'item'
              )
            }
          >
            Item Level
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-100"
            onClick={() => {
              setDateFrom(
                startOfYearYMD()
              );

              setDateTo(
                ymd(Date.now())
              );
            }}
          >
            YTD
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-100"
            onClick={() => {
              const d =
                new Date();

              const s = `${
                d.getFullYear()
              }-${String(
                d.getMonth() +
                  1
              ).padStart(
                2,
                '0'
              )}-01`;

              setDateFrom(s);

              setDateTo(
                ymd(Date.now())
              );
            }}
          >
            This Month
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-100"
            onClick={() => {
              const t =
                ymd(Date.now());

              setDateFrom(t);
              setDateTo(t);
            }}
          >
            Today
          </button>

          <button
            className="px-3 py-2 rounded bg-indigo-600 text-white disabled:opacity-50"
            onClick={
              exportPDF
            }
            disabled={
              loading ||
              (reportLevel ===
              'invoice'
                ? filtered.length ===
                  0
                : itemizedRows.length ===
                  0)
            }
          >
            Export PDF
          </button>

        </div>
      </div>

      {reportLevel === 'item' && <div className="flex justify-end mb-3">
        <button className="border rounded px-3 py-2 text-sm disabled:opacity-50" disabled={loading || !itemizedRows.length}
          onClick={() => downloadCsv(exportTable(), `${itemView === 'summary' ? 'ProductSummary' : 'ItemizedSales'}_${dateFrom}_${dateTo}.csv`)}>Export CSV</button>
      </div>}

      {rows.some(invoice => invoice.reportRefreshRequired) && <p role="alert" className="text-amber-900 bg-amber-50 p-3 mb-3 rounded">Order item data could not be matched safely. Reload the report for current quantities, prices and payment totals. Reload before relying on this snapshot for operational pending quantities.</p>}
      <button className="border rounded px-3 py-1.5 mb-3 disabled:opacity-50" disabled={loading} onClick={load}>Reload report</button>
      {/* FILTERS */}

      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-4 mb-4">

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              From
            </label>

            <input aria-label="From"
              type="date"
              className="w-full border px-2 py-1 rounded"
              value={
                dateFrom
              }
              onChange={(e) =>
                setDateFrom(
                  e.target.value
                )
              }
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              To
            </label>

            <input aria-label="To"
              type="date"
              className="w-full border px-2 py-1 rounded"
              value={
                dateTo
              }
              onChange={(e) =>
                setDateTo(
                  e.target.value
                )
              }
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Organization
            </label>

            <select aria-label="Organization"
              className="w-full border px-2 py-1 rounded"
              value={org}
              onChange={(e) =>
                setOrg(
                  e.target.value
                )
              }
            >
              <option>
                All
              </option>

              <option>
                COG
              </option>

              <option>
                The Bengal Craft
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Due
            </label>

            <select aria-label="Due"
              className="w-full border px-2 py-1 rounded"
              value={due}
              onChange={(e) =>
                setDue(
                  e.target.value
                )
              }
            >
              <option>
                All
              </option>

              <option>
                Due
              </option>

              <option>
                No Due
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Search
            </label>

            <input aria-label="Search"
              className="w-full border px-2 py-1 rounded"
              placeholder="Customer / Invoice / WhatsApp"
              value={q}
              onChange={(e) =>
                setQ(
                  e.target.value
                )
              }
            />
          </div>

        </div>
      </div>

      {loadError && <p role="alert" className="mb-4 text-rose-700">{loadError}</p>}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-4 mb-4 flex flex-wrap items-center gap-4">
        <Link id="delivery-management" className="border rounded px-3 py-1.5 text-sm text-indigo-700" to="/deliveries/bulk">Bulk Delivery</Link>

      </div>

      <div className="bg-white rounded-xl border p-4 mb-4 grid md:grid-cols-2 gap-3">
        <label className="text-sm">Item / Product search<input aria-label="Item / Product search" className="w-full border px-2 py-1 rounded mt-1" placeholder="Item description, Product Master name or SKU" value={itemQuery} onChange={event => setItemQuery(event.target.value)} /></label>
        <label className="text-sm">Delivery Status<select aria-label="Delivery Status" className="w-full border px-2 py-1 rounded mt-1" value={itemStatus} onChange={event => setItemStatus(event.target.value)}>
          {['All', 'Pending Delivery', 'Partially Delivered', 'Fully Delivered'].map(status => <option key={status}>{status}</option>)}
        </select></label>
        <p className="md:col-span-2 text-xs text-gray-500">Product and delivery status filters match individual items. Invoice Level shows invoices containing those items, with whole-invoice amounts. Due / No Due is always applied to invoices before expanding items.</p>
      </div>
      {productError && <p role="status" className="text-amber-800 mb-4">{productError}</p>}
      <div className="bg-white rounded-xl border p-4 mb-4 flex flex-wrap items-center gap-4">
        <label className="text-sm">Mapping Status<select aria-label="Mapping Status" className="border px-2 py-1 rounded ml-2" value={mappingFilter} onChange={event => setMappingFilter(event.target.value)}>
          {['All', 'Mapped', 'Partially Mapped', 'Unmapped', 'Mapping Incomplete'].map(status => <option key={status}>{status}</option>)}
        </select></label>
        <span className="text-sm text-gray-600" role="status">{catalogReady ? `${fullyMapped} of ${mappingCandidates.length} invoices fully mapped` : 'Mapping progress unavailable until Product Master data loads.'}</span>
        <span className="text-xs text-gray-500">Progress and Next Incomplete use the other active report filters, before the mapping filter.</span>
      </div>
      {/* ITEM SORT */}

      {reportLevel ===
        'item' && (
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-4 mb-4">

          <div className="flex flex-wrap items-end gap-4">
            <label className="text-sm">Item display<select aria-label="Item display" className="block border px-2 py-1 rounded mt-1" value={itemView} onChange={event => setItemView(event.target.value)}><option value="rows">Item Rows</option><option value="summary">Product Summary</option></select></label>
          <div className="max-w-xs">

            <label className="block text-sm text-gray-600 mb-1">
              Sort Itemized Report By
            </label>

            <select
              className="w-full border px-2 py-1 rounded"
              aria-label="Sort Itemized Report By"
              disabled={itemView === 'summary'}
              value={
                itemSort
              }
              onChange={(e) =>
                setItemSort(
                  e.target.value
                )
              }
            >
              <option value="item">
                Item
              </option>

              <option value="date">
                Date
              </option>

              <option value="order">
                Order Number
              </option>

              <option value="customer">
                Customer
              </option>
            </select>

          </div>
          {itemView === 'summary' && <p className="text-xs text-gray-500">Summary sorted by product name; products with different IDs remain separate.</p>}
          </div>
        </div>
      )}

      {/* INVOICE LEVEL */}

      {reportLevel ===
        'invoice' && (
        <>
          <div className="overflow-x-auto bg-white rounded-xl shadow ring-1 ring-gray-100">

            <table role="table" className="running-invoice-table min-w-full text-sm" aria-label="Invoice report">

              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">
                    Date
                  </th>

                  <th className="px-3 py-2 text-left">
                    Invoice
                  </th>

                  <th className="px-3 py-2 text-left">
                    Customer
                  </th>

                  <th className="px-3 py-2 text-left">
                    WhatsApp
                  </th>

                  <th className="px-3 py-2 text-left">
                    Org
                  </th>

                  <th className="px-3 py-2 text-left">
                    Mode
                  </th>

                  <th className="px-3 py-2 text-right">
                    Qty
                  </th>

                  <th className="px-3 py-2 text-right">
                    Invoice (INR)
                  </th>

                  <th className="px-3 py-2 text-right">
                    Received (INR)
                  </th>

                  <th className="px-3 py-2 text-right">
                    Due (INR)
                  </th>

                  <th className="px-3 py-2 text-left">Delivery</th>
                  <th className="px-3 py-2 text-left">Mapping</th>


                </tr>
              </thead>

              <tbody>

                {filtered.map(
                  (r) => {
                    const rowDue =
                      r.balanceDue !=
                      null
                        ? Number(
                            r.balanceDue
                          )
                        : Math.max(
                            Number(
                              r.totalAmount ||
                                0
                            ) -
                              Number(
                                r.amountReceived ||
                                  0
                              ),
                            0
                          );

                    const qty =
                      Array.isArray(
                        r.items
                      )
                        ? r.items.reduce(
                            (
                              q,
                              it
                            ) =>
                              q +
                              (Number(
                                it?.qty
                              ) ||
                                1),
                            0
                          )
                        : 0;

                    return (
                      <React.Fragment key={r._id}>
                      <tr role="row" className="invoice-data-row border-t">

                        <td data-label="Date" className="px-3 py-2">
                          {displayDate(r.invoiceDate || r.date)}
                        </td>

                        <td data-label="Invoice" className="px-3 py-2">
                          {r.invoiceNumber ||
                            r._id}
                        </td>

                        <td data-label="Customer" className="px-3 py-2">
                          {r.customerName ||
                            '-'}
                        </td>

                        <td data-label="WhatsApp" className="px-3 py-2">
                          {r.whatsapp ||
                            '-'}
                        </td>

                        <td data-label="Organization" className="px-3 py-2">
                          {r.organization ||
                            '-'}
                        </td>

                        <td data-label="Payment mode" className="px-3 py-2">
                          {r.paymentMode ||
                            '-'}
                        </td>

                        <td data-label="Quantity" className="px-3 py-2 text-right">
                          {qty}
                        </td>

                        <td data-label="Invoice (INR)" className="px-3 py-2 text-right">
                          {inr(
                            r.totalAmount
                          )}
                        </td>

                        <td data-label="Received (INR)" className="px-3 py-2 text-right">
                          {inr(
                            r.amountReceived
                          )}
                        </td>

                        <td data-label="Due (INR)" className="px-3 py-2 text-right">
                          {inr(
                            rowDue
                          )}
                        </td>

                        <td data-label="Delivery" className="px-3 py-2">
                          <span className={`inline-block rounded px-2 py-1 text-xs ${!isDeliveryReviewed(r) ? 'bg-amber-50 text-amber-800' : deliveryStatus(r) === 'Fully Delivered' ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>{deliveryStatus(r)}</span>
                        </td>
                        <td data-label="Mapping" className="px-3 py-2"><span className={`rounded px-2 py-1 text-xs ${mappingStatus(r, mappingProducts, catalogReady) === 'Mapped' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'}`}>{mappingStatus(r, mappingProducts, catalogReady)}</span></td>
                      </tr>
                      <tr role="row" className="invoice-actions-row" aria-label={`Invoice actions for ${r.invoiceNumber || r._id}`}>
                        <td colSpan={12}>
                          <div role="group" className="invoice-actions text-xs" aria-label={`Actions for ${r.invoiceNumber || r._id}`}>
                            <button className="px-2 py-1.5 text-indigo-700 hover:bg-gray-50" onClick={() => setViewing(r)}>View</button>
                            {r.invoiceNumber ? <Link className="px-2 py-1.5 text-indigo-700 hover:bg-gray-50" to={`/payments/running?invoice=${encodeURIComponent(r.invoiceNumber)}`}>Edit</Link> : <span className="px-2 py-1.5 text-gray-400" title="An invoice number is required by the existing editor">Edit</span>}
                            <button className="px-2 py-1.5 text-indigo-700 hover:bg-gray-50" onClick={() => setDeliveryId(r._id)}>Delivery</button>
                            <button className="px-2 py-1.5 text-indigo-700 hover:bg-gray-50" onClick={() => downloadReceipt(r)}>Receipt</button>
                            <button className="px-2 py-1.5 text-indigo-700 hover:bg-gray-50" onClick={() => setMappingId(r._id)}>Map Products</button>
                          </div>
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  }
                )}

                {filtered.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={
                        12
                      }
                      className="px-3 py-8 text-center text-gray-500"
                    >
                      No running sales in the selected range.
                    </td>
                  </tr>
                )}

              </tbody>

              <tfoot>

                <tr className="border-t bg-gray-50 font-semibold">

                  <td
                    className="px-3 py-2"
                    colSpan={
                      6
                    }
                  >
                    Totals
                  </td>

                  <td data-label="Total quantity" className="px-3 py-2 text-right">
                    {
                      totals.qty
                    }
                  </td>

                  <td data-label="Invoice (INR)" className="px-3 py-2 text-right">
                    {inr(
                      totals.invoice
                    )}
                  </td>

                  <td data-label="Received (INR)" className="px-3 py-2 text-right">
                    {inr(
                      totals.received
                    )}
                  </td>

                  <td data-label="Due (INR)" className="px-3 py-2 text-right">
                    {inr(
                      totals.due
                    )}
                  </td>

                  <td className="invoice-empty-total" />
                  <td className="invoice-empty-total" />

                </tr>

              </tfoot>
            </table>
          </div>

        </>
      )}

      {reportLevel === 'item' && <RunningSaleItems rows={itemView === 'summary' ? productSummary : itemizedRows}
        summary={itemView === 'summary'} totals={itemTotals} />}

      {mappingId && <ProductMappingPanel key={mappingId} invoiceId={mappingId} onClose={() => setMappingId(null)}
        onChanged={updateMappings} onCatalogChanged={updateMappingCatalog}
        onNext={nextMapping ? () => setMappingId(nextMapping._id) : undefined} />}
      {viewing && <InvoicePreview invoice={viewing} onClose={() => setViewing(null)} showDelivery
        onOpenDelivery={id => { setViewing(null); setDeliveryId(id); }} />}
      {deliveryId && <DeliveryPanel key={deliveryId} invoiceId={deliveryId} onClose={() => setDeliveryId(null)}
        onChanged={updateDelivery}
        onNext={nextPending ? () => setDeliveryId(nextPending._id) : undefined} />}

    </div>
  );
}
